import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AppConfigFacade } from '@navfleet/client/app-config/domain/data-access';
import { AuthService, AuthenticationFacade } from '@navfleet/client/authentication/domain/data-access';
import { CookieConsentContainer } from '@navfleet/client/cookie-consent/feature';
import { AppUpdateService } from '@navfleet/client/core/app-update';
import { filterFalsy, filterTruthy } from '@navfleet/client/shared/util/functions';
import { TenantFacade } from '@navfleet/client/tenant/domain/data-access';
import { ViewSettingsContainer } from '@navfleet/client/view-settings/feature';
import { AppInsights } from '@navfleet/shared/app-insights';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';

@Component({
    selector: 'fl-root',
    imports: [
        RouterOutlet,
        CookieConsentContainer,
        ViewSettingsContainer,
        ConfirmDialogModule,
        ButtonDirective,
        TranslatePipe,
    ],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent {
    private readonly authenticationFacade = inject(AuthenticationFacade);
    private readonly appConfigFacade = inject(AppConfigFacade);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly appUpdateService = inject(AppUpdateService);
    private readonly tenantFacade = inject(TenantFacade);

    constructor() {
        this.authenticationFacade.initAutoLogin();
        this.appUpdateService.init();

        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                this.authenticationFacade.isAuthenticated$
                    .pipe(filterFalsy(), take(1))
                    .subscribe(() => this.authService.redirectToExternalLogin());
                this.authenticationFacade.initAutoLogin();
            }
        });

        this.initAppInsightsLogic();
    }

    private initAppInsightsLogic(): void {
        this.appConfigFacade.appInsightsKey$
            .pipe(
                filterTruthy(),
                take(1),
                tap((key) => AppInsights.initialize(key)),
                switchMap(() =>
                    combineLatest([
                        this.authenticationFacade.getUserGuid(),
                        this.tenantFacade.selectedTenant$.pipe(map((tenant) => tenant?.id)),
                    ])
                ),
                distinctUntilChanged(
                    ([previousUserGuid, previousTenantId], [userGuid, tenantId]) =>
                        previousUserGuid === userGuid && previousTenantId === tenantId
                ),
                takeUntilDestroyed()
            )
            .subscribe(([userGuid, tenantId]) =>
                AppInsights.setAuthenticatedUserContext(userGuid, tenantId?.toString())
            );

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe((event: NavigationEnd) => {
                AppInsights.trackPageView({
                    name: event.urlAfterRedirects,
                    uri: window.location.href,
                });
            });
    }
}
